import { createPortal } from 'react-dom'
import { X, CheckCircle2, AlertCircle, AlertTriangle, Info } from 'lucide-react'
import { useToastStore, type ToastVariant } from '@/store/toast'
import { cn } from '@/utils/cn'

// ── Per-variant styles ─────────────────────────────────────────────────────────

const ICONS: Record<ToastVariant, React.ElementType> = {
  success: CheckCircle2,
  error:   AlertCircle,
  warning: AlertTriangle,
  info:    Info,
}

const STYLES: Record<ToastVariant, { wrap: string; icon: string; text: string; close: string; progress: string }> = {
  success: {
    wrap:     'bg-green-500 border-green-600 shadow-green-300/40',
    icon:     'text-white',
    text:     'text-white',
    close:    'text-green-100 hover:text-white hover:bg-green-600/50',
    progress: 'bg-green-300/60',
  },
  error: {
    wrap:     'bg-red-500 border-red-600 shadow-red-300/40',
    icon:     'text-white',
    text:     'text-white',
    close:    'text-red-100 hover:text-white hover:bg-red-600/50',
    progress: 'bg-red-300/60',
  },
  warning: {
    wrap:     'bg-amber-400 border-amber-500 shadow-amber-300/40',
    icon:     'text-amber-900',
    text:     'text-amber-900',
    close:    'text-amber-700 hover:text-amber-900 hover:bg-amber-500/40',
    progress: 'bg-amber-600/40',
  },
  info: {
    wrap:     'bg-blue-500 border-blue-600 shadow-blue-300/40',
    icon:     'text-white',
    text:     'text-white',
    close:    'text-blue-100 hover:text-white hover:bg-blue-600/50',
    progress: 'bg-blue-300/60',
  },
}

// ── Component ──────────────────────────────────────────────────────────────────

export function Toaster() {
  const toasts  = useToastStore((s) => s.toasts)
  const exiting = useToastStore((s) => s.exiting)
  const dismiss = useToastStore((s) => s.dismiss)

  if (toasts.length === 0) return null

  return createPortal(
    <div
      aria-live="polite"
      aria-atomic="false"
      className="fixed top-4 inset-x-0 z-[9999] flex flex-col items-center gap-2.5 pointer-events-none px-4"
    >
      {toasts.map((t) => {
        const Icon     = ICONS[t.variant]
        const s        = STYLES[t.variant]
        const isLeaving = exiting.includes(t.id)

        return (
          <div
            key={t.id}
            role="alert"
            className={cn(
              // Base
              'pointer-events-auto relative overflow-hidden',
              'flex w-full max-w-[420px] items-start gap-3',
              'rounded-xl border px-4 py-3.5 shadow-xl',
              s.wrap,
              // Animation
              isLeaving
                ? 'animate-out slide-out-to-top-3 fade-out-0 duration-300 fill-mode-forwards'
                : 'animate-in slide-in-from-top-3 fade-in-0 duration-300',
            )}
          >
            {/* Icon */}
            <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', s.icon)} />

            {/* Message */}
            <p className={cn('flex-1 text-sm font-medium leading-snug', s.text)}>
              {t.message}
            </p>

            {/* Dismiss button */}
            <button
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss"
              className={cn(
                'shrink-0 rounded-md p-0.5 transition-colors',
                s.close,
              )}
            >
              <X className="h-4 w-4" />
            </button>

            {/* Progress bar (drains left to right over durationMs) */}
            {!isLeaving && (
              <span
                className={cn('absolute bottom-0 left-0 h-[3px] rounded-full', s.progress)}
                style={{
                  width: '100%',
                  animation: `toast-drain ${t.durationMs}ms linear forwards`,
                }}
              />
            )}
          </div>
        )
      })}
    </div>,
    document.body
  )
}
